import {
  Component,
  computed,
  EventEmitter,
  Input,
  Output,
  QueryList,
  signal,
  ViewChildren,
} from '@angular/core';
import { CarouselComponent } from '../../carousel';
import { NodeItemComponent } from '../node-item/node-item.component';
import { SearchNodeComponent } from '../search-node/search-node.component';
export interface TreeNode {
  id: number;
  name: string;
  nomeTipologia?: string;
  expanded?: boolean;
  children: TreeNode[];
  childrenLoaded?: boolean;
  parentId?: number;
}

@Component({
  selector: 'siat-tree',
  imports: [NodeItemComponent, SearchNodeComponent, CarouselComponent],
  templateUrl: './tree-container.component.html',
  styleUrls: ['./tree-container.component.scss'],
})
export class TreeContainerComponent {
  /**
   * The tree data input.
   */
  @Input() set treeData(value: TreeNode[]) {
    this._treeData.set(value);
  }

  /**
   * The tree data signal.
   */
  private _treeData = signal<TreeNode[]>([]);

  /**
   * Title of the tree.
   */
  @Input()
  title: string;

  /**
   * The root id.
   */
  @Input()
  idStruttura: number;

  /**
   * Enable or disable search bar.
   */
  @Input()
  searchBar = true;

  /**
   * The last node click event emitter.
   */
  @Output()
  lastNodeClickEvent = new EventEmitter<{
    node: TreeNode;
    backgroundColor: string;
  }>();

  /**
   * The node change event emitter.
   */
  @Output()
  nodeChange = new EventEmitter<TreeNode>();

  /**
   * The add node event emitter.
   */
  @Output()
  onAddNodeEventEmitter = new EventEmitter<number | null>();

  /**
   * The update node event emitter.
   */
  @Output()
  onClickUpdateNodeEventEmitter = new EventEmitter<TreeNode>();

  /**
   * The delete node event emitter.
   */
  @Output()
  onClickDeleteNodeEventEmitter = new EventEmitter<TreeNode>();

  /**
   * Emitted when a node is clicked and its children have not been loaded yet.
   */
  @Output()
  loadChildren = new EventEmitter<TreeNode>();

  /**
   * The search node event emitter.
   */
  @ViewChildren(CarouselComponent)
  carousels!: QueryList<CarouselComponent<TreeNode>>;

  /**
   * Levels computed by tree data and number of items.
   */
  levels = computed(() => {
    const data = this._treeData();
    const lv = this.calculateLevels(data);

    if (data.length === 0) return [[]];

    const deepestExpandedLevel = this.findDeepestExpandedLevel(data);

    if (deepestExpandedLevel >= lv.length) lv.push([]);
    return lv;
  });

  /**
   * Calculate expanded levels.
   */
  calculateLevels(treeData: TreeNode[]): TreeNode[][] {
    const levels: TreeNode[][] = [];
    const traverseAndExpand = (nodes: TreeNode[], level: number): void => {
      levels[level] = levels[level] || [];
      nodes.forEach((node: TreeNode) => {
        levels[level].push(node);
        if (node.expanded && node.children.length > 0) {
          traverseAndExpand(node.children, level + 1);
        }
      });
    };

    traverseAndExpand(treeData, 0);
    return levels;
  }

  /**
   * On click node.
   */
  toggleNode(node: TreeNode, levelIndex: number): void {
    const levels = this.levels();
    levels[levelIndex].forEach(n => (n.expanded = false));
    node.expanded = true;

    this._treeData.set([...this._treeData()]);

    if (node.childrenLoaded === false) {
      this.loadChildren.emit(node);
      return;
    }

    if (!node.children || node.children.length === 0) {
      this.lastNodeClickEvent.emit({
        node,
        backgroundColor: this.getColor(levelIndex),
      });
    } else {
      this.nodeChange.emit(node);
    }
  }

  /**
   * Sets the children of a node by id, marking it as loaded, and refreshes the tree.
   */
  setNodeChildren(nodeId: number, children: TreeNode[]): void {
    const updateNode = (nodes: TreeNode[]): boolean => {
      for (const n of nodes) {
        if (n.id === nodeId) {
          n.children = children;
          n.childrenLoaded = true;
          return true;
        }
        if (n.children?.length && updateNode(n.children)) return true;
      }
      return false;
    };
    const data = this._treeData();
    updateNode(data);
    this._treeData.set([...data]);
  }

  /**
   * Search and select node.
   */
  searchAndSelectNode(selectedItemIndex: number, levelIndex: number): void {
    const findCarousel = this.carousels.find(
      carousel => carousel.carouselId === levelIndex
    );
    const selectedNode = findCarousel!.items[selectedItemIndex];

    this.toggleNode(selectedNode, levelIndex);
  }

  /**
   * @returns the expanded index.
   */
  getExpandedIndex(levelItems: TreeNode[]): number | null {
    const expanded = levelItems.findIndex(item => item.expanded);
    return expanded >= 0 ? expanded : null;
  }

  /**
   * Get colors for the tree.
   */
  getColor(index: number, areArrow = false): string {
    const colors = ['#dee2e6', '#abc7ff', '#d7e3ff'];
    const arrowColors = index === 0 ? '#dee2e6' : colors[index % colors.length];
    return areArrow
      ? arrowColors
      : index === 0
        ? 'transparent'
        : colors[(index - 1) % colors.length];
  }

  /**
   * Get the parent node.
   */
  getParentNode(level: number): TreeNode | null {
    const parent =
      level === 0
        ? null
        : (this.levels()[level - 1]?.find(node => node.expanded) ?? null);
    return parent;
  }

  /**
   * On click add node.
   */
  onClickAddNode(levelIndex: number): void {
    const parent = this.getParentNode(levelIndex)?.id || this.idStruttura;

    this.onAddNodeEventEmitter.emit(parent);
  }

  /**
   * Find deepest expanded level.
   */
  private findDeepestExpandedLevel(nodes: TreeNode[], level = 0): number {
    const expandedNode = nodes.find(n => n.expanded);
    if (!expandedNode) {
      return level;
    }
    if (expandedNode.children?.length) {
      return this.findDeepestExpandedLevel(expandedNode.children, level + 1);
    }
    return level + 1;
  }
}
